/**
 * EN-11016: forward tiles for recurring tournaments.
 *
 * A recurring tournament is ONE row that the engine reschedules in place once
 * it finishes (same tournamentId, `startAtTime` stepped forward — see
 * serverless-v2/shared/games/texas/tournaments/index.js, "reschedule the next
 * one"). So the lobby only ever holds its next occurrence, and the calendar went
 * blank past about a day for dailies and a week for weeklies. The client already
 * receives each tournament's `schedule`, so it projects the later occurrences
 * itself.
 *
 * Display only. A forward tile cannot be registered for (EN-10858 waits for real
 * instances); it shows when registration will open instead, from the
 * tournament's own `registrationOpens`, as the engine will apply it.
 */

/**
 * Calendar days ahead a forward tile may land (Deep Stack & The Grind,
 * 2026-09-30). Not also capped at the last announced tournament, as the
 * epic's AC2 reads: that left a calendar of dailies with no tiles at all, the
 * blank calendar the epic exists to fix (Neil, 2026-10-16).
 */
export const PROJECTION_HORIZON_DAYS = 30;

/** `tournaments.registrationOpens` when the row does not say: the column default. */
const DEFAULT_REGISTRATION_OPENS_MINS = 120;

/**
 * How long before its start an occurrence opens registration: the tournament's
 * own `registrationOpens` (minutes), as the engine applies it
 * (`tournamentAnnounceLogic`: Announced → Registering at
 * `startAtTime - registrationOpens * 60`). 0 is the engine's "open as soon as
 * announced", returned as 0 for the caller to handle.
 */
const registrationLeadSecs = (raw: any): number => {
  const mins = Number(raw?.registrationOpens);
  return (Number.isFinite(mins) && mins >= 0 ? mins : DEFAULT_REGISTRATION_OPENS_MINS) * 60;
};

/**
 * True when a projected occurrence's registration waits on the game before it:
 * its window would open at or before `previousStart`, or it opens as soon as
 * announced (`registrationOpens` 0). The engine keeps one row per recurring
 * tournament and reschedules it onto the next occurrence only after the one
 * before it FINISHES (finishTime + 5 min), and a finish time is not known
 * ahead — so the client can say only that it opens after that game.
 */
const opensAfterPreviousGame = (startAt: number, leadSecs: number, previousStart: number) =>
  leadSecs <= 0 || startAt - leadSecs <= previousStart;

/**
 * The engine's own steps, in UTC because that is the clock it reschedules on (a
 * Lambda). `Once3Months` and `OnceYear` are absent on purpose: their next
 * occurrence is always past the horizon, and `Once` / `Rolling` do not recur on
 * a calendar.
 */
const RECURRENCE_STEPS: Record<string, (date: Date) => void> = {
  OnceDay: (date) => date.setUTCDate(date.getUTCDate() + 1),
  OnceWeek: (date) => date.setUTCDate(date.getUTCDate() + 7),
  OnceMonth: (date) => date.setUTCMonth(date.getUTCMonth() + 1)
};

// A row whose start is long past (never rescheduled) must not spin the loop.
const MAX_STEPS = 400;

/** Calendar day key ("YYYY-MM-DD") of a Date on the device's local calendar. */
export const localDayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

const titleOf = (raw: any): string =>
  String(raw?.tournamentName || raw?.tablename || raw?.name || raw?.title || '')
    .trim()
    .toLowerCase();

const isCancelled = (raw: any): boolean => String(raw?.status || '').toUpperCase() === 'C';

/**
 * The prize pool the engine broadcasts for this game with nobody registered
 * yet (apps/engine/socket-broadcast-worker.js): the guarantee when there is
 * one; otherwise a Bounty's is its entries' pool (none yet, so 0) and any
 * other's is `minPlayers × buyIn`. The broadcast `buyIn` includes the entry
 * fee, which the engine's stored buy-in does not, so the fee comes back off.
 */
const emptyTablePrizePool = (raw: any): number => {
  const guarantee = Number(raw?.guarantee) || 0;
  if (guarantee > 0) return guarantee;
  if (String(raw?.gameType) === 'Bounty') return 0;
  const buyIn = (Number(raw?.buyIn) || 0) - (Number(raw?.entryFee) || 0);
  return Math.max(0, (Number(raw?.minPlayers) || 0) * buyIn);
};

/**
 * A satellite's seats for its empty-table pool. The broadcast ships the seat
 * count already divided out of the CURRENT pool (floor(pool / seatCost)) and
 * drops the seat cost, so it cannot be recomputed exactly. Scaling by
 * emptyPool / pool is a lower bound — exact while the pool is still the empty
 * table's — so a forward tile never promises more seats than that pool buys.
 * 0 for anything that is not a satellite.
 */
const emptyTableSatelliteSeats = (raw: any, emptyPool: number): number => {
  const seats = Number(raw?.satelliteSeatValue) || 0;
  const pool = Number(raw?.prizePool) || 0;
  if (seats <= 0 || pool <= 0 || emptyPool <= 0) return 0;
  return Math.floor((emptyPool * seats) / pool);
};

/**
 * A projected tile's id. Never a real tournamentId, so nothing live can match
 * it.
 */
export const projectedTournamentId = (sourceId: string, startAt: number): string =>
  `${sourceId}@${startAt}`;

/** The last day a forward tile may land on: 30 days from the engine's today. */
const horizonDayKey = (serverTime: number): string => {
  const today = new Date(serverTime * 1000);
  return localDayKey(
    new Date(today.getFullYear(), today.getMonth(), today.getDate() + PROJECTION_HORIZON_DAYS)
  );
};

/**
 * Lobby rows for every later occurrence of each recurring tournament, from now
 * through the horizon. Each row is the source row re-dated, emptied of its
 * current occurrence's state, and marked `isProjected`. A tile sits on the day
 * its occurrence is played (the team, 2026-10-19), and every occurrence gets
 * one: the engine plays each of them, rescheduling the row after every game.
 *
 * Needs the engine clock: with no `serverTime` there is no "today" to project
 * from, so nothing is projected (EN-10752 — never the device clock).
 *
 * Registration opens at the tournament's own `registrationOpens`, unless that
 * reaches back to the previous occurrence: then it opens after that game
 * finishes (opensAfterPreviousGame). (The epic's
 * 72 h / 3 h / 1 h by tournament type were an example, not a rule — Neil,
 * 2026-10-16.)
 *
 * A game is its title and start time: a real tournament with the same title
 * starting at the same moment replaces an occurrence, and two rows projecting
 * the same game show it once. Same-titled games at other times are their own.
 * Cancelled rows project nothing (accepted: their tiles disappear).
 */
export const projectRecurringTournaments = (rawList: any[], serverTime: number): any[] => {
  if (!(serverTime > 0)) return [];
  const liveRows = rawList.filter((raw) => !isCancelled(raw));
  const horizon = horizonDayKey(serverTime);

  // The games real tournaments already are, and those already projected.
  const gameOf = (raw: any, startAt: number) => `${titleOf(raw)}|${startAt}`;
  const realGames = new Set(
    liveRows
      .filter((raw) => Number(raw?.startAtTime) > 0)
      .map((raw) => gameOf(raw, Number(raw.startAtTime)))
  );
  const projectedGames = new Set<string>();

  const projected: any[] = [];
  for (const raw of liveRows) {
    const step = RECURRENCE_STEPS[String(raw?.schedule)];
    const sourceStart = Number(raw?.startAtTime) || 0;
    if (!step || sourceStart <= 0) continue;

    const sourceId = String(raw?.tournamentId ?? raw?.id);
    const leadSecs = registrationLeadSecs(raw);
    const date = new Date(sourceStart * 1000);
    let previousStart = sourceStart;
    for (let i = 0; i < MAX_STEPS; i++) {
      step(date);
      const startAt = Math.floor(date.getTime() / 1000);
      if (localDayKey(date) > horizon) break;
      const afterPrevious = opensAfterPreviousGame(startAt, leadSecs, previousStart);
      const opensAt = afterPrevious ? previousStart : startAt - leadSecs;
      previousStart = startAt;
      if (startAt <= serverTime) continue;
      const game = gameOf(raw, startAt);
      if (realGames.has(game) || projectedGames.has(game)) continue;
      projectedGames.add(game);
      projected.push(
        projectOccurrence(raw, sourceId, startAt, { opensAt, afterPrevious }, serverTime)
      );
    }
  }
  return projected;
};

const projectOccurrence = (
  raw: any,
  sourceId: string,
  startAt: number,
  { opensAt, afterPrevious }: { opensAt: number; afterPrevious: boolean },
  serverTime: number
) => ({
  ...raw,
  tournamentId: projectedTournamentId(sourceId, startAt),
  id: projectedTournamentId(sourceId, startAt),
  startAtTime: startAt,
  startTime: startAt,
  status: 'A',
  // Nobody can be in an occurrence that does not exist yet.
  registeredPlayers: 0,
  seatsTaken: 0,
  activePlayers: 0,
  empty: 1,
  full: 0,
  registrationOpen: 0,
  // The source's pool is its CURRENT occurrence's (entries included once it
  // fills); a future occurrence is promised the empty table's, as the engine
  // will broadcast it once the row is rescheduled onto it.
  prizePool: emptyTablePrizePool(raw),
  satelliteSeatValue: emptyTableSatelliteSeats(raw, emptyTablePrizePool(raw)),
  isProjected: true,
  projectedFromId: sourceId,
  // 0 once the window is already behind the clock: registration then opens as
  // soon as the real instance appears (the source finishes and is rescheduled),
  // so a date in the past would be wrong. Rebuilt each minute, so it flips.
  registrationOpensAt: opensAt > serverTime ? opensAt : 0,
  // registrationOpensAt is then the previous game's START, a lower bound: the
  // window opens once that game finishes, at a time not known ahead.
  registrationOpensAfterPrevious: afterPrevious
});
